import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { latestAddresses, howToAccess, features, stats } from '@/mocks/addresses';
import { faqList } from '@/mocks/faq';
import { categories } from '@/mocks/categories';

function copyToClipboard(text: string, callback: () => void) {
  navigator.clipboard.writeText(text).then(callback).catch(() => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    callback();
  });
}

export default function Home() {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCountdown(now.toLocaleString('zh-CN', { hour12: false }));
    };
    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleCopy = (id: string, url: string) => {
    copyToClipboard(url, () => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '91n.con 官方入口导航',
        url: 'https://www.91n.con',
        description: '提供91n.con最新可用地址、访问指引及常见问题解答',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'support@91n.con',
          contactType: 'customer support',
        },
      },
      {
        '@type': 'WebSite',
        name: '91n.con 官方入口导航',
        url: 'https://www.91n.con',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://www.91n.con/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: '91n.con官方入口·最新地址导航',
        description: '91n.con是用户热搜的访问入口关键词，本页提供91n.con的最新可用地址、访问指引及常见问题解答。',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91n.con/' },
          ],
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqList.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer.replace(/<[^>]*>/g, ''),
          },
        })),
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navbar />
      <ScrollToTop />

      <main>
        <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20stylish%20abstract%20digital%20landscape%20with%20warm%20orange%20and%20deep%20teal%20gradient%20waves%2C%20geometric%20network%20nodes%20connected%20by%20thin%20lines%20forming%20a%20global%20pattern%2C%20soft%20diffused%20light%20with%20warm%20glow%20effects%2C%20clean%20minimal%20aesthetic%2C%20dark%20ambient%20atmosphere%2C%20no%20text%20or%20letters%2C%204k%20quality%2C%20cinematic%20mood&width=1600&height=900&seq=hero-bg-91n&orientation=landscape"
            alt="91n.con 官方入口导航背景"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/20 backdrop-blur-sm border border-primary-400/30 mb-6">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                <span className="text-sm text-background-50/90">
                  最后更新: {countdown || '加载中...'}
                </span>
              </div>

              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight mb-5">
                91n.con 官方入口导航
              </h1>
              <p className="text-base md:text-lg text-background-50/80 max-w-2xl mx-auto mb-8 leading-relaxed">
                提供91n.con最新可用地址、访问指引及常见问题解答，帮助您快速找到官方渠道，安全高效直达目标页面
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#addresses"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-rocket-line"></i>
                  查看最新地址
                </a>
                <a
                  href="#faq"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg bg-background-50/15 text-background-50 backdrop-blur-sm border border-background-50/20 font-medium text-sm hover:bg-background-50/25 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-question-answer-line"></i>
                  常见问题
                </a>
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
            <a
              href="#addresses"
              className="flex flex-col items-center gap-2 text-background-50/60 hover:text-background-50 transition-colors cursor-pointer"
            >
              <span className="text-xs">向下滚动</span>
              <i className="ri-arrow-down-s-line text-lg animate-bounce"></i>
            </a>
          </div>
        </section>

        <section className="py-10 bg-background-50" id="stats">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100" id="addresses">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  最新可用地址
                </h2>
                <p className="text-foreground-500 max-w-xl mx-auto">
                  以下地址均已验证可用，请根据所在地区选择最优入口。如遇访问问题，可尝试切换其他节点。
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
                {latestAddresses.map((addr) => (
                  <div
                    key={addr.id}
                    className="group bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-primary-300/50 transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                          <i className="ri-global-line text-lg"></i>
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-foreground-900 text-sm">
                            {addr.label}
                          </h3>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${addr.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                            <span className="text-xs text-foreground-400">
                              {addr.status === 'online' ? '正常' : '维护中'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-background-100 rounded-lg px-3.5 py-2.5 mb-3 flex items-center justify-between">
                      <code className="text-xs text-foreground-700 font-mono truncate">
                        {addr.url}
                      </code>
                      <button
                        onClick={() => handleCopy(addr.id, addr.url)}
                        className="ml-2 w-8 h-8 flex items-center justify-center rounded-md bg-background-50 hover:bg-primary-50 text-foreground-500 hover:text-primary-600 transition-all flex-shrink-0 cursor-pointer"
                        title="复制地址"
                      >
                        <i className={`text-sm ${copiedId === addr.id ? 'ri-check-line text-green-500' : 'ri-file-copy-line'}`}></i>
                      </button>
                    </div>

                    <div className="flex items-center gap-4 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-speed-mini-line"></i>
                        {addr.speed}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-line"></i>
                        {addr.region}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {addr.updatedAt}
                      </span>
                    </div>

                    <p className="mt-3 text-xs text-foreground-500 leading-relaxed">
                      {addr.description}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-5 bg-accent-50 border border-accent-200/70 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      <strong className="text-foreground-900">温馨提示：</strong>
                      以上地址信息实时同步官方最新数据。如发现某节点不可用，请尝试其他节点或等待5-10分钟后刷新页面。所有地址均为91n.con官方发布，请勿通过非官方渠道获取地址信息，谨防钓鱼风险。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50" id="how-to">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  访问指引
                </h2>
                <p className="text-foreground-500 max-w-xl mx-auto">
                  只需简单几步，即可快速找到并访问91n.con官方入口
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {howToAccess.map((step) => (
                  <div key={step.step} className="relative text-center group">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                      <i className={`${step.icon} text-2xl`}></i>
                    </div>
                    <div className="absolute top-8 -right-4 hidden lg:block">
                      {step.step < 4 && (
                        <i className="ri-arrow-right-line text-foreground-300 text-xl"></i>
                      )}
                    </div>
                    <div className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary-500 text-background-50 text-xs font-bold mb-3">
                      {step.step}
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">
                      {step.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100" id="features">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  为什么选择我们
                </h2>
                <p className="text-foreground-500 max-w-xl mx-auto">
                  我们致力于成为最可靠的91n.con访问入口导航平台
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {features.map((feat) => (
                  <div
                    key={feat.id}
                    className="bg-background-50 rounded-xl p-6 border border-background-200/70 hover:border-accent-200/50 transition-all duration-300 group"
                  >
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                      <i className={`${feat.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">
                      {feat.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      {feat.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50" id="categories">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  内容导航
                </h2>
                <p className="text-foreground-500 max-w-xl mx-auto">
                  浏览不同分类获取最全面的访问和使用指南
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="bg-background-100 hover:bg-background-200/70 rounded-xl p-5 border border-background-200/70 hover:border-primary-200/50 transition-all duration-300 group cursor-pointer"
                  >
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1.5">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed mb-3">
                      {cat.description}
                    </p>
                    <span className="text-xs text-primary-500 font-medium">
                      {cat.count} 篇文章
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100" id="faq">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  常见问题
                </h2>
                <p className="text-foreground-500">
                  快速找到您关心的问题和答案
                </p>
              </div>

              <div className="space-y-3">
                {faqList.map((faq) => (
                  <div
                    key={faq.id}
                    className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100/50 transition-colors"
                    >
                      <span className="font-heading font-semibold text-foreground-900 text-sm pr-4">
                        {faq.question}
                      </span>
                      <i
                        className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-300 flex-shrink-0 ${
                          openFaq === faq.id ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    <div
                      className={`px-5 overflow-hidden transition-all duration-300 ${
                        openFaq === faq.id ? 'pb-5 max-h-[500px]' : 'max-h-0'
                      }`}
                    >
                      <div
                        className="text-sm text-foreground-600 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 text-center">
                <Link
                  to="/category/faq-collection"
                  className="inline-flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer"
                >
                  查看全部常见问题
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                收藏本页，随时获取最新地址
              </h2>
              <p className="text-foreground-500 mb-8 max-w-xl mx-auto">
                将本导航页添加到浏览器书签，即可随时获取91n.con最新官方地址和访问指引。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#addresses"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-links-line"></i>
                  立即获取地址
                </a>
                <Link
                  to="/about"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg bg-secondary-100 text-secondary-800 font-medium text-sm hover:bg-secondary-200 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-information-line"></i>
                  了解更多
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}